import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { listitemContract } from './listitem.contract'

const VARIANTS = listitemContract.props.variant.values
const SIZES = listitemContract.props.size.values

export type ListItemVariant = (typeof VARIANTS)[number]
export type ListItemSize = (typeof SIZES)[number]

export type ListItemProps = {
    children: ReactNode
    variant?: ListItemVariant
    size?: ListItemSize
    /** Figma's `checkbox` variant leaves room for one; the caller supplies it. */
    leading?: ReactNode
    className?: string
    testID?: string
}

export const LIST_ITEM_VARIANT = variantForStyleId(listitemContract.textStyleIds[0])

/**
 * One row of a dropdown or a filter list.
 *
 * Figma pads the row on the right only — 0 left, `medium-less-dense/l` right,
 * the same in all four variants — which the importer used to refuse outright as
 * asymmetric. It is deliberate: the row's left edge lines up with the field
 * above it and the right padding keeps the label off the scrollbar.
 *
 * The width Figma draws is the sample frame, not a constraint a list can use,
 * so a caller passes `w-full` through `className`. That is on the list for
 * design — see DESIGN-TODO.md.
 */
const variantFor = (variant: ListItemVariant, size: ListItemSize) => {
    const key = `variant=${variant}, size=${size}`
    const found = (
        listitemContract.variants as Record<
            string,
            { container: string; label: string; box: string }
        >
    )[key]
    if (!found) {
        throw new Error(`aos-ds ListItem: the Figma contract has no "${key}"`)
    }
    return found
}

export const ListItem = ({
    children,
    variant = 'default',
    size = 'big',
    leading,
    className,
    testID,
}: ListItemProps) => {
    const { container, label, box } = variantFor(variant, size)
    /**
     * The hover overlay is part of the row, not a layer over it.
     *
     * Figma paints it on a `background` child that exists only in the `hover`
     * variant, so the contract carries it under `box` with a `hover:` prefix —
     * and this component read `container` and `label` and quietly ignored it.
     * The row had no hover state on screen at all, and nothing failed: a
     * contract field a component never reads is invisible from both ends.
     *
     * Applied to the row itself because the overlay covers exactly the row; the
     * sizes it carries are the row's own, so `twMerge` collapses them.
     */
    return (
        <View
            className={twMerge('flex-row items-center', container, box, className)}
            testID={testID ?? `list-item-${variant}-${size}`}
        >
            {leading}
            <Typography variant={LIST_ITEM_VARIANT} className={label} testID='list-item-label'>
                {children}
            </Typography>
        </View>
    )
}

ListItem.displayName = 'ListItem'

export { SIZES as LIST_ITEM_SIZES, VARIANTS as LIST_ITEM_VARIANTS }
